import { useEffect, useRef, useState } from 'react'
import { revealWhenKeyboardSettles } from '../lib/keyboard'
import { COLOR_KEYS, ICON_KEYS, CategoryIcon, colorVar } from './CategoryIcon'
import './CategoryEditor.css'

export interface CategoryDraft {
  label: string
  color: string
  icon: string
}

const BLANK: CategoryDraft = { label: '', color: 'green', icon: 'tag' }

/** Label + colour + icon, inline. Shared by the create-on-the-fly row in
 * AddReminder and the per-row editor in Settings so the two can't drift. */
export function CategoryEditor({
  initial,
  submitLabel,
  busy = false,
  error = null,
  autoFocus = false,
  onSubmit,
  onCancel,
}: {
  initial?: CategoryDraft
  submitLabel: string
  busy?: boolean
  error?: string | null
  autoFocus?: boolean
  onSubmit: (draft: CategoryDraft) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState<CategoryDraft>(initial ?? BLANK)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const swatch = colorVar(draft.color)
  const ready = draft.label.trim().length > 0 && !busy

  // Autofocusing the name field opens the keyboard, and on iOS the AutoFill
  // "Contact" bar rides above it — together tall enough to bury the colour and
  // icon rows that sit below the input (APP-07). Scroll the whole panel into
  // view once that has settled so every control stays reachable. Scrolling the
  // panel rather than the input is deliberate: the browser's own
  // scroll-on-focus only guarantees the *field* is visible, which is precisely
  // the behaviour that left the rest of the form underneath the keyboard.
  useEffect(() => {
    if (!autoFocus) return
    return revealWhenKeyboardSettles(panelRef.current)
  }, [autoFocus])

  return (
    <div className="ced glass" ref={panelRef} style={{ ['--cat' as string]: swatch }}>
      {error && (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      )}

      <input
        className="ced__label"
        value={draft.label}
        autoFocus={autoFocus}
        maxLength={40}
        placeholder="Category name"
        aria-label="Category name"
        onFocus={() => revealWhenKeyboardSettles(panelRef.current)}
        onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && ready) onSubmit({ ...draft, label: draft.label.trim() })
          if (e.key === 'Escape') onCancel()
        }}
      />

      <div className="ced__swatches" role="radiogroup" aria-label="Colour">
        {COLOR_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={draft.color === key}
            aria-label={key}
            className={`ced__swatch${draft.color === key ? ' is-on' : ''}`}
            style={{ ['--sw' as string]: colorVar(key) }}
            onClick={() => setDraft((d) => ({ ...d, color: key }))}
          />
        ))}
      </div>

      <div className="ced__icons" role="radiogroup" aria-label="Icon">
        {ICON_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={draft.icon === key}
            aria-label={key}
            className={`ced__icon${draft.icon === key ? ' is-on' : ''}`}
            onClick={() => setDraft((d) => ({ ...d, icon: key }))}
          >
            <CategoryIcon icon={key} size={21} />
          </button>
        ))}
      </div>

      <div className="ced__actions">
        <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={!ready}
          onClick={() => onSubmit({ ...draft, label: draft.label.trim() })}
        >
          {busy ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  )
}
